import type { TOC, TOCItem, TOCParent } from '~/common/types';

// Legacy hardcoded source (parity reference) seeding the active singletons;
// applyDbDigitizationBundle refills them from the DB bundle, and no delivery restores these.

const LEGACY_DIGITIZATION_LAYER_IDS: string[] = [
  'digitizedElectricVaults',
  'digitizedElectricStructures',
  'digitizedElectricLines',
  'digitizedGasPoints',
  'digitizedGasLines',
  'digitizedSewerPoints',
  'digitizedSewerLines',
  'digitizedWaterPoints',
  'digitizedWaterLines',
  'digitizedRecycledWaterLines',
  'digitizedRecycledWaterPoints',
  'digitizedStormPoints',
  'digitizedStormLines',
  'digitizedRailroadLines',
  'digitizedRailroadPoints',
  'digitizedTelcoLines',
  'digitizedTelcoPoints',
  'digitizedEasements',
  'digitizedFuelPoints',
  'digitizedFuelLines',
  'digitizedTrafficSignals',
  'digitizedTrafficSignalLines',
  'digitizedOtherPoints',
  'digitizedOtherLines',
];

const LEGACY_DIGITIZATION_TOC: TOC = [
  {
    sectionId: 'digitizedLayers',
    label: 'Digitized Layers',
    expanded: true,
    children: [
      {
        sectionId: 'digitizedElectric',
        label: 'Electric',
        expanded: true,
        children: [
          { id: 'digitizedElectricVaults', label: 'Vaults' },
          { id: 'digitizedElectricStructures', label: 'Structures' },
          { id: 'digitizedElectricLines', label: 'Lines' },
        ],
      },
      {
        sectionId: 'digitizedGas',
        label: 'Gas',
        expanded: true,
        children: [
          { id: 'digitizedGasPoints', label: 'Point Assets' },
          { id: 'digitizedGasLines', label: 'Lines' },
        ],
      },
      {
        sectionId: 'digitizedSewer',
        label: 'Sewer',
        expanded: true,
        children: [
          { id: 'digitizedSewerPoints', label: 'Point Assets' },
          { id: 'digitizedSewerLines', label: 'Lines' },
        ],
      },
      {
        sectionId: 'digitizedWater',
        label: 'Water',
        expanded: true,
        children: [
          { id: 'digitizedWaterPoints', label: 'Point Assets' },
          { id: 'digitizedWaterLines', label: 'Lines' },
        ],
      },
      {
        sectionId: 'digitizedRecycledWater',
        label: 'Recycled Water',
        expanded: true,
        children: [
          { id: 'digitizedRecycledWaterPoints', label: 'Point Assets' },
          { id: 'digitizedRecycledWaterLines', label: 'Lines' },
        ],
      },
      {
        sectionId: 'digitizedStorm',
        label: 'Storm',
        expanded: true,
        children: [
          { id: 'digitizedStormPoints', label: 'Point Assets' },
          { id: 'digitizedStormLines', label: 'Lines' },
        ],
      },
      {
        sectionId: 'digitizedRailroad',
        label: 'Railroad',
        expanded: true,
        children: [
          { id: 'digitizedRailroadLines', label: 'Lines' },
          { id: 'digitizedRailroadPoints', label: 'Points' },
        ],
      },
      {
        sectionId: 'digitizedTelco',
        label: 'Telco',
        expanded: true,
        children: [
          { id: 'digitizedTelcoPoints', label: 'Point Assets' },
          { id: 'digitizedTelcoLines', label: 'Lines' },
        ],
      },
      {
        sectionId: 'digitizedLandRights',
        label: 'Land Rights',
        expanded: true,
        children: [{ id: 'digitizedEasements', label: 'Easements' }],
      },
      {
        sectionId: 'digitizedFuel',
        label: 'Fuel',
        expanded: true,
        children: [
          { id: 'digitizedFuelPoints', label: 'Point Assets' },
          { id: 'digitizedFuelLines', label: 'Lines' },
        ],
      },
      {
        sectionId: 'digitizedTraffic',
        label: 'Streetlights & Traffic Signals',
        expanded: true,
        children: [
          { id: 'digitizedTrafficSignals', label: 'Signals' },
          { id: 'digitizedTrafficSignalLines', label: 'Lines' },
        ],
      },
      {
        sectionId: 'digitizedOther',
        label: 'Other',
        expanded: true,
        children: [
          { id: 'digitizedOtherPoints', label: 'Point Assets' },
          { id: 'digitizedOtherLines', label: 'Lines' },
        ],
      },
    ],
  },
];

/**
 * Digitization parent label → `sectionId`, re-attached to the DB-derived tree by label
 * (DB rows don't carry `sectionId`). `sectionId` is required: download branching keys
 * off `sectionId.includes('digitized')` (new-download/store.ts), and the TOC widget uses it for
 * expand state + test ids. Labels are unique within the digitization subtree. Kept code-side
 * (not a DB column) — it is client-only presentation plumbing.
 */
const DIGITIZATION_SECTION_IDS: Record<string, string> = {
  'Digitized Layers': 'digitizedLayers',
  Electric: 'digitizedElectric',
  Gas: 'digitizedGas',
  Sewer: 'digitizedSewer',
  Water: 'digitizedWater',
  'Recycled Water': 'digitizedRecycledWater',
  Storm: 'digitizedStorm',
  Railroad: 'digitizedRailroad',
  Telco: 'digitizedTelco',
  'Land Rights': 'digitizedLandRights',
  Fuel: 'digitizedFuel',
  'Streetlights & Traffic Signals': 'digitizedTraffic',
  Other: 'digitizedOther',
};

function isTOCParent(item: TOCItem): item is TOCParent {
  return 'children' in item;
}

// Active singletons. Refilled in place (`.length = 0; .push(...)`) so binding identity stays
// stable — call-time consumers and `TOCLayers.composeActiveToc` keep working, mirroring `TOC`.

export const digitizationLayerIds: string[] = [...LEGACY_DIGITIZATION_LAYER_IDS];
export const digitizationTOC: TOC = structuredClone(LEGACY_DIGITIZATION_TOC);

/**
 * Attach `sectionId` to every parent by label. Returns `false` on any unmapped label — a DB
 * label drift must fail the whole apply and revert to legacy rather than install a half-broken
 * tree. Mutates `nodes` in place; only the caller commits it.
 */
function attachSectionIds(nodes: TOC): boolean {
  for (const node of nodes) {
    if (!isTOCParent(node)) continue;
    const sectionId = DIGITIZATION_SECTION_IDS[node.label];
    if (!sectionId) return false;
    node.sectionId = sectionId;
    if (!attachSectionIds(node.children)) return false;
  }
  return true;
}

function fill(ids: string[], toc: TOC): void {
  digitizationLayerIds.length = 0;
  digitizationLayerIds.push(...ids);
  digitizationTOC.length = 0;
  digitizationTOC.push(...toc);
}

/**
 * Refill the active singletons from the DB-derived bundle delivered at bootstrap.
 * The bundle's tree carries no `sectionId`, so re-attach by label first.
 */
export function applyDbDigitizationBundle(bundle: { digitizationLayerIds: string[]; digitizationTOC: TOC }): void {
  const toc = structuredClone(bundle.digitizationTOC);
  if (!attachSectionIds(toc)) {
    // Unmapped parent label — don't install a degraded tree.
    resetDigitizationToLegacy();
    return;
  }
  fill(bundle.digitizationLayerIds, toc);
}

/**
 * Restore the active singletons to the legacy source. The no-delivery branch must call this on
 * every org switch so a previous org's DB bundle can't leak through.
 */
export function resetDigitizationToLegacy(): void {
  fill(LEGACY_DIGITIZATION_LAYER_IDS, structuredClone(LEGACY_DIGITIZATION_TOC));
}
